import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { formatMedida, formatDate } from "@/lib/format";
import { useLabelPrint } from "@/context/LabelPrintContext";
import { Search, Undo2, Loader2, Printer, Truck, Package, CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const inputCls =
  "h-12 w-full rounded-md border-2 border-slate-300 px-3 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

const STATUS_CLS = {
  Pendente: "bg-amber-100 text-amber-800",
  "Assinado e retornado": "bg-green-100 text-green-800",
  Perdido: "bg-red-100 text-red-800",
};

const genSessionId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export default function Devolucao() {
  const { printLabel, printLabels } = useLabelPrint();
  const [romaneios, setRomaneios] = useState([]);
  const [q, setQ] = useState("");
  const [rom, setRom] = useState(null);
  const [pesos, setPesos] = useState({});
  const [busy, setBusy] = useState("");
  const [sessionRolos, setSessionRolos] = useState([]);
  const [entradaCodigoAtual, setEntradaCodigoAtual] = useState(null);
  const [labelSel, setLabelSel] = useState([]);
  const sessionIdRef = useRef(genSessionId());

  useEffect(() => {
    api.get("/romaneios").then((r) => setRomaneios(r.data)).catch(() => {});
  }, []);

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return romaneios;
    return romaneios.filter((r) =>
      [r.codigo, r.ref, r.cliente, r.destino].filter(Boolean).some((v) => String(v).toLowerCase().includes(t))
    );
  }, [romaneios, q]);

  const grupos = useMemo(() => {
    if (!rom) return [];
    const map = new Map();
    rom.itens.forEach((i) => {
      const k = i.combinacao_id || `${i.cor}|${i.fornecedor_nome}`;
      if (!map.has(k))
        map.set(k, {
          combinacao_id: i.combinacao_id,
          cor: i.cor,
          tipo: i.tipo,
          fornecedor_nome: i.fornecedor_nome,
          unidade: i.unidade,
          bloco: i.bloco,
          rolos: 0,
          total: 0,
        });
      const g = map.get(k);
      g.rolos += 1;
      g.total += Number(i.peso || 0);
    });
    return [...map.values()];
  }, [rom]);

  const resetSessao = ({ toastMsg = null } = {}) => {
    sessionIdRef.current = genSessionId();
    setSessionRolos([]);
    setLabelSel([]);
    setEntradaCodigoAtual(null);
    if (toastMsg) toast.success(toastMsg);
  };

  const selecionar = async (r) => {
    try {
      const res = await api.get(`/romaneios/${r.id}`);
      setRom(res.data);
      setPesos({});
      // Trocar de romaneio abre nova sessão (novo ENT).
      resetSessao();
    } catch (e) {
      const _m = apiError(e); if (_m) toast.error(_m);
    }
  };

  const registrar = async (g) => {
    const key = g.combinacao_id || `${g.cor}|${g.fornecedor_nome}`;
    const raw = pesos[key] || "";
    const p = parseFloat(String(raw).replace(",", "."));
    if (!p || p <= 0) {
      toast.error("Informe o peso/metragem devolvido");
      return;
    }
    setBusy(key);
    try {
      const res = await api.post("/romaneios/devolucao", {
        romaneio_id: rom.id,
        combinacao_id: g.combinacao_id,
        peso_devolvido_kg: p,
        session_id: sessionIdRef.current,
      });
      setSessionRolos((prev) => [...prev, res.data]);
      if (res.data?.entrada_codigo) setEntradaCodigoAtual(res.data.entrada_codigo);
      setPesos((prev) => ({ ...prev, [key]: "" }));
      toast.success(`Devolução registrada: ${res.data.codigo} — ${formatMedida(res.data.peso_atual_kg, res.data.unidade)}`);
    } catch (e) {
      const _m = apiError(e); if (_m) toast.error(_m);
    } finally {
      setBusy("");
    }
  };

  const toggleLabel = (id) =>
    setLabelSel((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const allLabelsChecked = sessionRolos.length > 0 && labelSel.length === sessionRolos.length;
  const toggleAllLabels = () => setLabelSel(allLabelsChecked ? [] : sessionRolos.map((r) => r.id));

  const printSelectedLabels = () => {
    const arr = sessionRolos.filter((r) => labelSel.includes(r.id));
    if (arr.length === 0) return toast.error("Marque as etiquetas que quer imprimir");
    printLabels(arr);
  };

  return (
    <div data-testid="devolucao-page">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
          <Undo2 size={28} /> Devolução
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Escolha o <strong>romaneio/REF</strong> de origem, veja o que saiu por cor e vá registrando os rolos que voltaram.
          Todos ficam num <strong>único documento de entrada (ENT)</strong> — como na Entrada normal.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Seleção de romaneio */}
        <section className="rounded-md border-2 border-slate-200 bg-white p-5">
          <label className="mb-1 block text-sm font-semibold text-slate-700">Buscar romaneio (nº, REF, cliente, destino)</label>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className={`${inputCls} pl-9`}
              placeholder="ex: 3393 ou Carlos"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="devolucao-busca"
            />
          </div>

          <div className="mt-3 max-h-[28rem] overflow-y-auto" data-testid="devolucao-romaneios-list">
            {filtrados.length === 0 && (
              <p className="py-6 text-center text-sm text-slate-400">Nenhum romaneio encontrado.</p>
            )}
            <ul className="divide-y divide-slate-100">
              {filtrados.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => selecionar(r)}
                    data-testid={`devolucao-rom-${r.codigo}`}
                    className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-2.5 text-left hover:bg-slate-50 ${
                      rom?.id === r.id ? "bg-blue-50 ring-1 ring-blue-300" : ""
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-slate-900">{r.codigo}</span>
                        <span className="text-sm text-slate-600">REF {r.ref}</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {r.cliente || "—"} · {r.destino} · {formatDate(r.data)} · {r.total_rolos} rolo(s)
                      </div>
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_CLS[r.status] || "bg-slate-100 text-slate-700"}`}>
                      {r.status}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Itens enviados + registrar devolução */}
        <section className="rounded-md border-2 border-slate-200 bg-white p-5">
          {!rom ? (
            <div className="flex h-full min-h-[12rem] flex-col items-center justify-center text-center text-slate-400">
              <Truck size={40} className="mb-2 opacity-40" />
              <p className="text-sm">Selecione um romaneio para ver o que saiu e registrar as devoluções.</p>
            </div>
          ) : (
            <div data-testid="devolucao-itens">
              <div className="mb-3 rounded-md bg-slate-50 p-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-lg font-bold text-slate-900">{rom.codigo}</span>
                  <span className="text-sm text-slate-600">REF {rom.ref}</span>
                </div>
                <div className="text-xs text-slate-500">
                  {rom.cliente || "—"} · {rom.descricao_peca || "—"} · destino {rom.destino}
                </div>
              </div>

              <div className="space-y-3">
                {grupos.map((g) => {
                  const key = g.combinacao_id || `${g.cor}|${g.fornecedor_nome}`;
                  return (
                    <div key={key} className="rounded-md border-2 border-slate-100 p-3" data-testid={`devolucao-grupo-${g.cor}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-bold text-slate-900">
                            {g.cor} <span className="font-normal text-slate-500">({g.fornecedor_nome})</span>
                          </div>
                          <div className="text-xs text-slate-500">
                            {g.tipo} · {g.bloco === "ribana" ? "Ribana" : "Principal"} · enviado: {g.rolos} rolo(s) ·{" "}
                            {formatMedida(g.total, g.unidade)}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 flex gap-2">
                        <input
                          className="h-11 flex-1 rounded-md border-2 border-slate-300 px-3 text-base outline-none focus:border-blue-500"
                          placeholder={`Devolução (${g.unidade === "m" ? "m" : "kg"})`}
                          inputMode="decimal"
                          value={pesos[key] || ""}
                          onChange={(e) => setPesos((prev) => ({ ...prev, [key]: e.target.value }))}
                          data-testid={`devolucao-peso-${g.cor}`}
                        />
                        <button
                          onClick={() => registrar(g)}
                          disabled={busy === key}
                          data-testid={`devolucao-registrar-${g.cor}`}
                          className="flex items-center gap-2 rounded-md bg-slate-900 px-4 font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                        >
                          {busy === key ? <Loader2 className="animate-spin" size={16} /> : <Undo2 size={16} />}
                          Registrar
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Bloco "Cadastrados nesta sessão" — mesmo padrão da Entrada normal */}
      {rom && (
        <section className="mt-6 rounded-md border-2 border-slate-200 bg-white p-5" data-testid="devolucao-session-section">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                <CheckCircle2 size={20} className="text-amber-600" /> Cadastrados nesta sessão
              </h2>
              {entradaCodigoAtual && (
                <div className="mt-1 text-xs text-slate-500" data-testid="devolucao-doc-atual">
                  Documento em aberto:{" "}
                  <span className="font-mono font-bold text-amber-700">{entradaCodigoAtual}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {sessionRolos.length > 0 && (
                <button
                  type="button"
                  onClick={() => resetSessao({ toastMsg: "Nova devolução iniciada — próximos rolos irão para um novo documento" })}
                  data-testid="devolucao-nova-sessao"
                  className="flex items-center gap-1 rounded-md border-2 border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                >
                  <RotateCcw size={14} /> Nova devolução
                </button>
              )}
              <span className="rounded-md bg-slate-900 px-3 py-1 text-sm font-bold text-white" data-testid="devolucao-counter">
                {sessionRolos.length} rolo{sessionRolos.length === 1 ? "" : "s"}
              </span>
            </div>
          </div>

          {sessionRolos.length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={allLabelsChecked}
                    onChange={toggleAllLabels}
                    data-testid="devolucao-label-select-all"
                    className="h-4 w-4 accent-slate-900"
                  />
                  Marcar todas ({labelSel.length}/{sessionRolos.length})
                </label>
                <button
                  onClick={printSelectedLabels}
                  data-testid="devolucao-print-selected"
                  className="flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800"
                >
                  <Printer size={16} /> Imprimir selecionadas ({labelSel.length})
                </button>
              </div>
              <button
                onClick={() => printLabels(sessionRolos)}
                data-testid="devolucao-print-all"
                className="flex w-full items-center justify-center gap-2 rounded-md border-2 border-slate-900 bg-white px-3 py-2.5 text-sm font-bold text-slate-900 hover:bg-slate-50"
              >
                <Printer size={18} /> Imprimir todas ({sessionRolos.length})
              </button>
            </div>
          )}

          <ul className="mt-4 divide-y divide-slate-100" data-testid="devolucao-session-list">
            {sessionRolos.length === 0 && (
              <li className="py-6 text-center text-sm text-slate-400">Nenhum rolo devolvido ainda nesta sessão.</li>
            )}
            {sessionRolos.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-3" data-testid={`devolucao-session-rolo-${r.codigo}`}>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={labelSel.includes(r.id)}
                    onChange={() => toggleLabel(r.id)}
                    data-testid={`devolucao-label-check-${r.codigo}`}
                    className="h-4 w-4 accent-slate-900"
                  />
                  <div>
                    <div className="font-mono font-bold text-slate-900">{r.codigo}</div>
                    <div className="text-xs text-slate-500">
                      {r.tipo} {r.cor} <span className="text-slate-400">({r.fornecedor_nome})</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-amber-700">{formatMedida(r.peso_atual_kg, r.unidade)}</span>
                  <button
                    onClick={() => printLabel(r)}
                    data-testid={`devolucao-print-${r.codigo}`}
                    className="flex items-center gap-1 rounded-md border-2 border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    <Printer size={14} /> Etiqueta
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
